import { setContext } from "@apollo/client/link/context";
import { SmokeballAuthenticator } from "./smokeball-authenticator";
import { ApolloClient, InMemoryCache, createHttpLink, gql } from "@apollo/client";
import { mapFileFromGQL } from "../models/file-model";
import { Maybe } from "graphql/jsutils/Maybe";
import { filterFalsy } from "../helpers/TsHelpers";
import { mapFolderFromGQL } from "../models/folder-model";
import { ensureConfig } from "./configuration-service";

type TPageInfo = {
  total: number;
  taken: number;
  skipped: number;
  hasMore: boolean;
  pageSize: number;
};

const makePageInfo = (raw: Record<string, number | boolean | string>) => ({
  total: +raw.totalFiles || +raw.totalFolders || 0,
  taken: +raw.taken || 0,
  skipped: +raw.skipped || 0,
  pageSize: +raw.pageSize || 0,
  hasMore: !!raw.hasMore,
});

const getPagedResponse = <T>(paging: Maybe<TPageInfo>, collection: Maybe<T[]>, error: Maybe<string>) => ({
  hasError: !!error,
  error: error,
  data: collection || [],
  paging,
});

let client: ApolloClient<object> | null = null;

const getClient = () => {
  if (!client) {
    const regionConfig = ensureConfig();
    const uri = regionConfig.MatterManagementGraphQLUrl;

    console.log(`The URI for GraphQL is: ${uri}`);

    const httpLink = createHttpLink({
      uri: uri,
    });

    const authLink = setContext(async (_, { headers }) => {
      const authenticator = SmokeballAuthenticator.getSmokeballAuthenticator();
      const token = await authenticator.getTokenFromStorage();
      const newLocal = `Bearer ${token?.idtoken}`;

      return {
        headers: {
          ...headers,
          authorization: newLocal,
        },
      };
    });

    client = new ApolloClient({
      link: authLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
  }

  return client;
};

type TPagingParams = {
  skip?: number;
  take?: number;
  includeDeleted?: boolean;
};

type TPagingParamsWithFolder = TPagingParams & {
  folderId?: string;
};

const getMatterFilesForFolder = async (matterId: string, params?: TPagingParamsWithFolder) => {
  const getMatterFilesForFolderGql = gql`
    query GetMatterFilesForFolder(
      $folderId: String
      $matterId: String
      $includeDeleted: Boolean!
      $skip: Int!
      $take: Int!
    ) {
      matterFilesForFolder(
        folderId: $folderId
        matterId: $matterId
        includeDeleted: $includeDeleted
        skip: $skip
        take: $take
      ) {
        totalFiles
        taken
        skipped
        hasMore
        pageSize
        files {
          id
          name
          folderId
          fileSizeBytes
          fileExtension
          dateCreated
          lastUpdatedUtc
          owner {
            firstName
            lastName
            colorFill
            colorStroke
          }
        }
      }
    }
  `;

  try {
    const result = await getClient().query({
      query: getMatterFilesForFolderGql,
      variables: {
        matterId: matterId,
        folderId: params?.folderId || null,
        includeDeleted: params?.includeDeleted || false,
        skip: params?.skip || 0,
        take: params?.take || 500,
      },
      fetchPolicy: "network-only",
    });

    if (result.error) {
      console.error(result.error);
      return getPagedResponse(null, [], JSON.stringify(result.error));
    }

    const rawFiles = result.data.matterFilesForFolder.files as unknown[];
    const files = rawFiles.map(mapFileFromGQL).filter(filterFalsy);
    const paging = makePageInfo(result.data.matterFilesForFolder);

    return getPagedResponse(paging, files, null);
  } catch (error) {
    console.error(error);

    return getPagedResponse(null, [], JSON.stringify(error));
  }
};

const getMatterFoldersForFolder = async (matterId: string, params?: TPagingParamsWithFolder) => {
  const getMatterFoldersForFolderGql = gql`
    query GetMatterFoldersForFolder(
      $parentFolderId: String
      $matterId: String
      $includeDeleted: Boolean!
      $skip: Int!
      $take: Int!
    ) {
      matterFoldersForFolder(
        parentFolderId: $parentFolderId
        matterId: $matterId
        includeDeleted: $includeDeleted
        skip: $skip
        take: $take
      ) {
        totalFolders
        taken
        skipped
        hasMore
        pageSize
        folders {
          id
          name
          parentId
          dateCreated
          dateModified
        }
      }
    }
  `;

  try {
    const result = await getClient().query({
      query: getMatterFoldersForFolderGql,
      variables: {
        matterId: matterId,
        parentFolderId: params?.folderId || null,
        includeDeleted: params?.includeDeleted || false,
        skip: params?.skip || 0,
        take: params?.take || 500,
      },
      fetchPolicy: "network-only",
    });

    if (result.error) {
      console.error(result.error);
      return getPagedResponse(null, [], JSON.stringify(result.error));
    }

    const rawFolders = result.data.matterFoldersForFolder.folders as unknown[];
    const folders = rawFolders.map((f) => mapFolderFromGQL(matterId, f)).filter(filterFalsy);
    const paging = makePageInfo(result.data.matterFoldersForFolder);

    return getPagedResponse(paging, folders, null);
  } catch (error) {
    console.error(error);

    return getPagedResponse(null, [], JSON.stringify(error));
  }
};

const getMatterFolders = async (matterId: string, params?: TPagingParams) => {
  const getMatterFoldersGql = gql`
    query GetMatterFolders($matterId: String, $includeDeleted: Boolean!, $skip: Int!, $take: Int!) {
      matterFolders(matterId: $matterId, includeDeleted: $includeDeleted, skip: $skip, take: $take) {
        totalFolders
        taken
        skipped
        hasMore
        pageSize
        folders {
          id
          name
          parentId
          dateCreated
          dateModified
        }
      }
    }
  `;

  try {
    const result = await getClient().query({
      query: getMatterFoldersGql,
      variables: {
        matterId: matterId,
        includeDeleted: params?.includeDeleted || false,
        skip: params?.skip || 0,
        take: params?.take || 500,
      },
      fetchPolicy: "network-only",
    });

    if (result.error) {
      console.error(result.error);
      return getPagedResponse(null, [], JSON.stringify(result.error));
    }

    const rawFolders = result.data.matterFolders.folders as unknown[];
    const folders = rawFolders.map((f) => mapFolderFromGQL(matterId, f)).filter(filterFalsy);
    const paging = makePageInfo(result.data.matterFolders);

    return getPagedResponse(paging, folders, null);
  } catch (error) {
    console.error(error);

    return getPagedResponse(null, [], JSON.stringify(error));
  }
};

// This defines which GraphQL query to use and what fields to return.
// See the following file for GraphQL definition for the "searchForFiles" query:
// https://bitbucket.org/smokeballdev/international/src/9f06502e0d482a2f47fc76faf77b5a2fc7b881ec/pd/matter%20management/ui/browse/Online.Desktop.GraphQL/schema.graphql#lines-29
// Note that the name of the query definition in the schema is "searchForFiles".
// The query name "SearchForFiles" is what we use at client side to label the operation, it will be sent as "operationName" in the request for identification purposes.
const searchForFiles = async (matterId: string, searchText: string) => {
  const searchForFilesGql = gql`
    query SearchForFiles($request: FileSearchRequestInput) {
      searchForFiles(request: $request) {
        inAccessibleCount
        accountId
        hitSize
        hasError
        accountId
        maxResults
        errorMessage
        results {
          fileId
          file {
            id
            folderId
            ownerId
            name
            fileExtension
            fileSizeBytes
            dateCreated
            lastUpdatedUtc
            deleted
            owner {
              id
              firstName
              lastName
              colorFill
              colorStroke
            }
          }
          matterId
          contentHighlight
          fileNameHighlight
        }
      }
    }
  `;

  // This query request must:
  // 1. Supply all the compulsory fields (fields marked with a ! in the schema).
  // 2. Use the enum values defined in the schema, e.g. fileRelationType must be one of the values defined in the FileRelation type in the schema.
  try {
    const result = await getClient().query({
      query: searchForFilesGql,
      variables: {
        request: {
          matterId,
          searchText,

          // The search result offset, i.e. how many documents to skip when searching, for paging purpose which we don't use at the moment.
          from: 0,

          // Using query syntax allows you to partially match file names at the cost of performance.
          useQuerySyntax: false,

          // This allows the QueryInfo field in the result to contain the actual JSON query sent to OpenSearch.
          // However, we are not requesting the QueryInfo field in the result, so this is not necessary.
          returnSearchQueryInfo: false,

          // searchByFields can be "NAME", "CONTENT" or "NAME_AND_CONTENT" according to the schema.
          searchByFields: "NAME",

          // fileRelationType can be "NONE" or "PARENT_EMAIL" according to the schema.
          // "PARENT_EMAIL" also returns the email that the file is attached to if the result contains any email attachments.
          fileRelationType: "NONE"
        },
      },

      // This fetchPolicy ensures that the query is always sent to the server and never read from the cache.
      fetchPolicy: "network-only",
    });

    if (result.error) {
      console.error(result.error);
      return getPagedResponse(null, [], JSON.stringify(result.error));
    }

    const rawFiles = result.data.searchForFiles.results as unknown[];
    const folders = rawFiles.map((meta) => mapFileFromGQL((meta as { file: unknown }).file)).filter(filterFalsy);

    return getPagedResponse(null, folders, null);
  } catch (error) {
    console.error(error);

    return getPagedResponse(null, [], JSON.stringify(error));
  }
};

export const getFilesRetriever = () => ({
  searchForFiles,
  getMatterFolders,
  getMatterFilesForFolder,
  getMatterFoldersForFolder,
});

export type TFilesRetriever = ReturnType<typeof getFilesRetriever>;
